/**
 * Money formatting for the admin app.
 *
 * App-level rather than feature-local because there were already two of these:
 * `currency` in the payments dashboard and a catalogue copy, disagreeing about
 * rounding, about null handling, and about whether the symbol is `₹` or `Rs.`
 * -- three different answers for the same paise, on screens an operator uses in
 * the same sitting.
 *
 * TWO UNITS, ONE PER DOMAIN. Catalogue prices are stored in paise; escrow,
 * milestone and quote amounts are stored in whole rupees (ADR 010, amended
 * 2026-09-26). Pick the formatter by the column, never by the screen: escrow
 * rupees read as paise show an operator a hundredth of the money held.
 */

/**
 * Paise as rupees.
 *
 * Fractional rupees survive. Rounding to whole rupees is right for a scannable
 * column and wrong the moment a real price list carries paise: an operator
 * comparing Rs 480.50 against Rs 481 would be reading a number the system does
 * not hold. All seeded prices happen to be whole rupees today, which is exactly
 * why the rounding looked harmless -- so the decimals appear only when there
 * are any, and whole rupees stay clean.
 */
export function formatPaise(paise: number | null | undefined): string {
	if (paise === null || paise === undefined || !Number.isFinite(paise)) {
		return "—";
	}
	return formatRupees(paise / 100);
}

/**
 * Rupees as rupees: escrow, milestone and quote amounts.
 *
 * Same rendering rules as `formatPaise`, which delegates here, so the two
 * units cannot drift apart in how they print.
 */
export function formatRupees(rupees: number | null | undefined): string {
	if (rupees === null || rupees === undefined || !Number.isFinite(rupees)) {
		return "—";
	}

	/*
	 * The SIGN GOES OUTSIDE the symbol.
	 *
	 * Prefixing "₹" to an already-signed number produced "₹-480", which is not
	 * how anyone writes money in any locale.
	 */
	const negative = rupees < 0;
	const magnitude = Math.abs(rupees);
	const rendered = magnitude.toLocaleString("en-IN", {
		minimumFractionDigits: Number.isInteger(magnitude) ? 0 : 2,
		maximumFractionDigits: 2,
	});
	return `${negative ? "-" : ""}₹${rendered}`;
}

/**
 * Rupees as typed, as integer paise, or `undefined` when it is not an amount.
 *
 * Read as a string rather than multiplied as a float: `0.29 * 100` is
 * `28.999999999999996`, which the server's integer check refuses.
 */
export function rupeesToPaise(text: string): number | undefined {
	const cleaned = text.replace(/,/g, "").trim();
	if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) return undefined;
	const [whole, fraction = ""] = cleaned.split(".");
	return Number(whole) * 100 + Number(fraction.padEnd(2, "0"));
}

/** Paise as the text of a rupees input: 9950 becomes "99.5". */
export function paiseToRupeeInput(paise: number): string {
	return String(paise / 100);
}

/**
 * A date that names a DAY, read in UTC so the reader's timezone cannot move it.
 *
 * A price's `observedAt` and an observation's `effectiveFrom` are days printed
 * on a price list, sent as midnight-UTC instants because JSON has no date type.
 * `toLocaleDateString()` without a zone renders them a day early for anyone west
 * of UTC (#256). It applies the same rule as `formatCalendarDate` in
 * apps/frontend; that one lives in another app.
 */
export function formatCalendarDate(value: string): string {
	const at = new Date(value);
	if (Number.isNaN(at.getTime())) return value;
	return at.toLocaleDateString("en-IN", {
		year: "numeric",
		month: "short",
		day: "numeric",
		timeZone: "UTC",
	});
}
